<template>
  <div>
    <div v-if="loading" class="pa-4">
      <v-skeleton-loader type="card" class="mb-4" />
      <v-skeleton-loader type="list-item@4" class="mb-4" />
      <v-skeleton-loader type="card" />
    </div>

    <template v-else>
      <v-card outlined class="pa-4 mb-4">
        <div class="recon-row">
          <span>{{ $t("finance_opening_balance") }}</span>
          <strong>{{ formatCurrency(openingBalance) }}</strong>
        </div>
        <div
          v-for="line in reconciliationLines"
          :key="line.label"
          class="recon-row"
          :class="line.kind"
        >
          <span>{{ line.kind === "credit" ? "+" : "-" }} {{ $t(line.labelKey) }}</span>
          <strong>{{ formatCurrency(line.amount) }}</strong>
        </div>

        <v-divider class="my-3" />

        <div class="recon-row total">
          <span>{{ $t("finance_current_balance") }}</span>
          <strong>{{ formatCurrency(closingBalance) }}</strong>
        </div>
      </v-card>

      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 d-block">
          <div class="d-flex flex-wrap align-center" style="gap: 12px">
            <span>{{ $t("finance_table_label") }}</span>
            <v-spacer />
            <dropdown
              v-model="filterPeriod"
              :items="periodOptions"
              item-text="label"
              item-value="value"
              :label="$t('finance_date')"
              hide-details
              style="max-width: 220px"
            />
            <dropdown
              v-model="filterCategory"
              :items="categoryOptions"
              item-text="label"
              item-value="value"
              :label="$t('finance_category')"
              hide-details
              style="max-width: 220px"
            />
          </div>
        </v-card-title>

        <v-data-table
          :headers="ledgerHeaders"
          :items="filteredEntries"
          mobile-breakpoint="0"
          item-key="id"
          :no-data-text="$t('finance_no_ledger_items_filtered')"
          class="striped-table"
        >
          <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
          <template v-slot:item.debit="{ item }">
            <span style="color: #d9820f; font-weight: 600">
              {{ item.amount < 0 ? formatCurrency(Math.abs(item.amount)) : "—" }}
            </span>
          </template>
          <template v-slot:item.credit="{ item }">
            <span style="color: #00b37e; font-weight: 600">
              {{ item.amount > 0 ? formatCurrency(item.amount) : "—" }}
            </span>
          </template>
          <template v-slot:item.balance="{ item }">
            <span class="font-weight-bold">{{ formatCurrency(item.balance) }}</span>
          </template>
        </v-data-table>
      </v-card>

      <v-card outlined class="pa-4">
        <p class="section-label">{{ $t("finance_bottom_label") }}</p>
        <v-row>
          <v-col cols="12" sm="4">
            <p class="footer-label">{{ $t("finance_opening_balance") }}</p>
            <p class="footer-value">{{ formatCurrency(openingBalance) }}</p>
          </v-col>
          <v-col cols="12" sm="4">
            <p class="footer-label">{{ $t("finance_changes") }}</p>
            <p class="footer-value" :class="netChange >= 0 ? 'positive' : 'negative'">
              {{ netChange >= 0 ? "+" : "" }}{{ formatCurrency(netChange) }}
            </p>
          </v-col>
          <v-col cols="12" sm="4">
            <p class="footer-label">{{ $t("finance_closing_balance") }}</p>
            <p class="footer-value">{{ formatCurrency(closingBalance) }}</p>
          </v-col>
        </v-row>
      </v-card>
    </template>
  </div>
</template>

<script>
import gpsApi from "@/plugins/gpsApi";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "FinanceTabBalanceLedger",

  mixins: [hasSnackbar],

  data: () => ({
    loading: true,
    openingBalance: 0,
    ledgerEntries: [],
    filterPeriod: "all",
    filterCategory: "all",
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    periodOptions() {
      return [
        { label: this.$t("finance_all"), value: "all" },
        { label: this.$t("finance_last_30_days"), value: "30d" },
        { label: this.$t("finance_last_90_days"), value: "90d" },
        { label: this.$t("finance_this_year"), value: "year" },
      ];
    },

    categoryOptions() {
      return [
        { label: this.$t("finance_all_categories"), value: "all" },
        { label: this.$t("finance_card_payments"), value: "cards" },
        { label: this.$t("finance_corrections"), value: "corrections" },
        { label: this.$t("finance_commissions_label"), value: "commission" },
        { label: this.$t("finance_delivery"), value: "delivery" },
        { label: this.$t("finance_marketing"), value: "marketing" },
        { label: this.$t("finance_refunds_label"), value: "refunds" },
        { label: this.$t("finance_invoices_label"), value: "invoices" },
        { label: this.$t("finance_other"), value: "other" },
      ];
    },

    ledgerHeaders() {
      return [
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_description"), value: "description" },
        { text: this.$t("finance_debit"), value: "debit", align: "end" },
        { text: this.$t("finance_credit"), value: "credit", align: "end" },
        { text: this.$t("finance_balance"), value: "balance", align: "end" },
      ];
    },

    entriesWithBalance() {
      let balance = this.openingBalance;
      return [...this.ledgerEntries]
        .sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)
        .map((entry) => {
          balance += entry.amount;
          return { ...entry, balance };
        })
        .reverse();
    },

    reconciliationLines() {
      const sumWhere = (predicate) =>
        this.ledgerEntries
          .filter((e) => predicate(e.description))
          .reduce((sum, e) => sum + Math.abs(e.amount), 0);

      return [
        { label: "cards", labelKey: "finance_card_payments", kind: "credit", amount: sumWhere((d) => d.startsWith("Kartične uplate")) },
        { label: "corrections", labelKey: "finance_corrections", kind: "credit", amount: sumWhere((d) => d.startsWith("Korekcija")) },
        { label: "commission", labelKey: "finance_commissions_label", kind: "debit", amount: sumWhere((d) => d === "Provizija") },
        { label: "delivery", labelKey: "finance_delivery", kind: "debit", amount: sumWhere((d) => d.startsWith("Naknada za dostavu")) },
        { label: "marketing", labelKey: "finance_marketing", kind: "debit", amount: sumWhere((d) => d.startsWith("Istaknuta pozicija")) },
        { label: "refunds", labelKey: "finance_refunds_label", kind: "debit", amount: sumWhere((d) => d.startsWith("Refundacija")) },
        { label: "invoices", labelKey: "finance_invoices_label", kind: "debit", amount: sumWhere((d) => d.startsWith("Faktura")) },
      ];
    },

    netChange() {
      return this.ledgerEntries.reduce((sum, e) => sum + e.amount, 0);
    },

    closingBalance() {
      return this.openingBalance + this.netChange;
    },

    filteredEntries() {
      return this.entriesWithBalance.filter(
        (entry) =>
          this.isWithinPeriod(entry.date, this.filterPeriod) &&
          (this.filterCategory === "all" || this.categorize(entry.description) === this.filterCategory)
      );
    },
  },

  created() {
    this.load();
  },

  methods: {
    formatCurrency(value) {
      return `${Number(value || 0).toFixed(2)} ${this.restaurantCurrency}`;
    },

    formatDate(isoDate) {
      return new Date(isoDate).toLocaleDateString("sr-RS", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
    },

    categorize(description) {
      if (description.startsWith("Kartične uplate")) return "cards";
      if (description.startsWith("Korekcija")) return "corrections";
      if (description === "Provizija") return "commission";
      if (description.startsWith("Naknada za dostavu")) return "delivery";
      if (description.startsWith("Istaknuta pozicija")) return "marketing";
      if (description.startsWith("Refundacija")) return "refunds";
      if (description.startsWith("Faktura")) return "invoices";
      return "other";
    },

    isWithinPeriod(date, value) {
      if (value === "all") return true;

      const target = new Date(date);
      const now = new Date();

      if (value === "year") return target.getFullYear() === now.getFullYear();

      const days = value === "30d" ? 30 : 90;
      return now.getTime() - target.getTime() <= days * 24 * 60 * 60 * 1000;
    },

    async load() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { data } = await gpsApi.get(`/restaurants/${this.restaurantId}/balance-ledger`);
        const payload = data?.data || {};
        this.openingBalance = payload.opening_balance || 0;
        this.ledgerEntries = payload.ledger_entries || [];
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_balance_ledger_load_error"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.recon-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: #6b7685;
  padding: 4px 0;
}

.recon-row.credit strong {
  color: #00b37e;
}

.recon-row.debit strong {
  color: #d9820f;
}

.recon-row.total {
  font-size: 1.15rem;
  color: #0b1220;
  font-weight: 700;
}

.recon-row.total strong {
  color: #0b1220;
}

.section-label {
  margin: 0 0 10px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9aa4b2;
}

.footer-label {
  margin: 0 0 4px;
  font-size: 0.75rem;
  color: #9aa4b2;
}

.footer-value {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 800;
  color: #0b1220;
}

.footer-value.positive {
  color: #00b37e;
}

.footer-value.negative {
  color: #d9820f;
}
</style>
